import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, TrendingUp } from 'lucide-react';
import { AuthContext, AppSettingsContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on mount
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    try {
      const response = await axios.get(`${API}/general-settings/public`);
      const settings = response.data;
      setContent(settings.privacy_policy || getDefaultPrivacyPolicy(settings));
    } catch (error) {
      console.error('Error fetching privacy policy:', error);
      setContent(getDefaultPrivacyPolicy({}));
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPrivacyPolicy = (settings) => {
    const contactEmail = settings?.contact_email || appSettings?.contact_email || 'support@oddschamp.com';
    const companyName = settings?.company_name || settings?.app_name || appSettings?.company_name || appSettings?.app_name || 'OddsChamp';
    
    return `
      <h2>Privacy Policy</h2>
      <p><strong>Last Updated:</strong> ${new Date().toLocaleDateString()}</p>
      
      <h3>1. Introduction</h3>
      <p>${companyName} is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our betting tips and analytics platform.</p>

      <h3>2. Information We Collect</h3>
      <p>We collect information you provide directly to us, including:</p>
      <ul>
        <li><strong>Account Information:</strong> Name, email address, password, profile picture, date of birth, country</li>
        <li><strong>Betting Data:</strong> Betting history, stakes, odds, outcomes, bookmakers, leagues, strategies, and performance statistics</li>
        <li><strong>Tipster Services:</strong> Tips created, purchased, subscribed services, reviews, ratings, and earnings</li>
        <li><strong>AI Predictions:</strong> Generated predictions, usage history, and preferences</li>
        <li><strong>Payment Information:</strong> Subscription plans, payment transactions, PayPal email (for tipsters), processed through Stripe and PayPal</li>
        <li><strong>Communications:</strong> Support tickets, feedback, reviews, and email preferences</li>
        <li><strong>Technical Data:</strong> IP address, browser type, device information, cookies, and usage analytics</li>
      </ul>

      <h3>3. How We Use Your Information</h3>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide betting tracking, analytics, and reporting features</li>
        <li>Deliver AI-powered betting predictions and insights</li>
        <li>Process subscriptions, tip purchases, and tipster earnings</li>
        <li>Facilitate tipster services and marketplace</li>
        <li>Send transactional notifications and service updates</li>
        <li>Process withdrawals and payments to tipsters</li>
        <li>Provide customer support and respond to inquiries</li>
        <li>Improve and personalize our services</li>
        <li>Analyze usage patterns and generate insights</li>
        <li>Detect and prevent fraud and security issues</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h3>4. Information Sharing and Disclosure</h3>
      <p>We do not sell your personal information. We may share information with:</p>
      <ul>
        <li><strong>Payment Processors:</strong> Stripe and PayPal for secure payment processing and payouts</li>
        <li><strong>Email Service Providers:</strong> For sending transactional and notification emails</li>
        <li><strong>Cloud Service Providers:</strong> For hosting, storage, and infrastructure (AWS, MongoDB, etc.)</li>
        <li><strong>Analytics Providers:</strong> For usage analytics and service improvement</li>
        <li><strong>Other Users:</strong> Your public profile, tips, and reviews may be visible to other users</li>
        <li><strong>Legal Authorities:</strong> When required by law or to protect rights and safety</li>
      </ul>

      <h3>5. Data Security</h3>
      <p>We implement appropriate technical and organizational security measures to protect your information, including:</p>
      <ul>
        <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
        <li>Secure password hashing</li>
        <li>Regular security audits</li>
        <li>Access controls and authentication</li>
        <li>Secure cloud infrastructure</li>
      </ul>
      <p>However, no method of transmission over the Internet is 100% secure. Please use strong passwords and keep your credentials confidential.</p>

      <h3>6. Your Privacy Rights</h3>
      <p>You have the right to:</p>
      <ul>
        <li><strong>Access:</strong> Request a copy of your personal data</li>
        <li><strong>Correct:</strong> Update inaccurate or incomplete information</li>
        <li><strong>Delete:</strong> Request deletion of your data (subject to legal retention requirements)</li>
        <li><strong>Object:</strong> Object to certain data processing activities</li>
        <li><strong>Export:</strong> Receive your data in a portable format</li>
        <li><strong>Withdraw Consent:</strong> Withdraw consent for marketing communications</li>
      </ul>
      <p>To exercise these rights, please contact us or manage your settings in your account profile.</p>

      <h3>7. Cookies and Tracking Technologies</h3>
      <p>We use cookies and similar technologies to:</p>
      <ul>
        <li>Maintain your login session</li>
        <li>Remember your preferences (currency, date format, odds format)</li>
        <li>Analyze usage patterns and improve our services</li>
        <li>Provide personalized content and recommendations</li>
      </ul>
      <p>You can manage cookie preferences through your browser settings.</p>

      <h3>8. Data Retention</h3>
      <p>We retain your personal data as follows:</p>
      <ul>
        <li>Account data: While your account is active and for 2 years after closure</li>
        <li>Betting history: 7 years for tax and regulatory compliance</li>
        <li>Payment records: 7 years for accounting requirements</li>
        <li>Marketing data: Until you withdraw consent or 2 years of inactivity</li>
      </ul>

      <h3>9. Third-Party Services</h3>
      <p>Our platform integrates with third-party services (Stripe, PayPal) that have their own privacy policies. We are not responsible for their privacy practices.</p>

      <h3>10. Children's Privacy</h3>
      <p>Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.</p>

      <h3>11. International Data Transfers</h3>
      <p>Your data may be transferred to and processed in countries outside your country of residence. We ensure appropriate safeguards are in place for such transfers.</p>

      <h3>12. Changes to This Policy</h3>
      <p>We may update this privacy policy from time to time. We will notify you of significant changes via email or through a prominent notice on our platform. Continued use after changes constitutes acceptance.</p>

      <h3>13. GDPR Compliance</h3>
      <p>For detailed information about our GDPR compliance and your rights under EU data protection law, please see our <a href="/gdpr" style="color: #60a5fa; text-decoration: underline;">GDPR Compliance</a> page.</p>

      <h3>14. Contact Us</h3>
      <p>If you have questions about this privacy policy or wish to exercise your privacy rights, please contact us at: <strong>${contactEmail}</strong></p>
    `;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 flex items-center justify-center">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </Layout>
    );
  }

  const pageContent = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <Card className="glass-dark border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center text-2xl">
              <Shield className="w-6 h-6 mr-2 text-blue-400" />
              Privacy Policy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="prose prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: content }}
              style={{
                color: '#e5e7eb',
                fontSize: '0.95rem',
                lineHeight: '1.7'
              }}
            />
            
            <div className="mt-8 pt-6 border-t border-white/10">
              {user ? (
                <Link 
                  to="/dashboard"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  ← Back to Dashboard
                </Link>
              ) : (
                <Link 
                  to="/"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  ← Back to Home
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // If user is logged in, use Layout, otherwise use public navigation
  if (user) {
    return <Layout>{pageContent}</Layout>;
  }

  // Public view with Landing page-style navigation
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Public Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-dark border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              {appSettings.app_logo_url ? (
                <img 
                  src={appSettings.app_logo_url} 
                  alt={appSettings.app_name} 
                  className="h-10 max-w-[200px] object-contain"
                />
              ) : (
                <>
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                  <span className="text-lg sm:text-xl font-bold whitespace-nowrap">{appSettings.app_name}</span>
                </>
              )}
            </div>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full"
            >
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Content with top padding for fixed nav */}
      <div className="pt-16">
        {pageContent}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
